import { useState } from "react";
import { FaPen, FaPlay, FaStop, FaTrashCan } from "react-icons/fa6";
import Select2 from "../../../components/Select2";
import Input from "../../main/components/Input";
import { trpc } from "../../main/components/trpc.ts";

interface RespondAnnouncesPanelProps {
  ownerId: string;
  announces: Array<{
    id: string;
    name: string;
    date: Date;
    duration: number;
  }>;
  announceSettings:
    | {
        slot_one: string | null;
        slot_two: string | null;
        slot_three: string | null;
        selected_slot: number;
      }
    | null
    | undefined;
  onChanged: () => void;
}

const slotKeyMap = {
  "0": "Aucun slot",
  "1": "Annonce 1",
  "2": "Annonce 2",
  "3": "Annonce 3",
} as const;

function slotFromSettings(
  announceId: string,
  settings: RespondAnnouncesPanelProps["announceSettings"],
) {
  if (!settings) {
    return "0" as const;
  }
  if (settings.slot_one === announceId) {
    return "1" as const;
  }
  if (settings.slot_two === announceId) {
    return "2" as const;
  }
  if (settings.slot_three === announceId) {
    return "3" as const;
  }
  return "0" as const;
}

function AnnounceRow(props: {
  ownerId: string;
  announce: RespondAnnouncesPanelProps["announces"][number];
  slot: "0" | "1" | "2" | "3";
  onChanged: () => void;
}) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [name, setName] = useState(props.announce.name);
  const [audio, setAudio] = useState<HTMLAudioElement | null>(null);

  const getAudioMutation = trpc.respond.getAudioAnnounceLinkForOwner.useMutation();
  const assignMutation = trpc.respond.assignAnnounceToSlotForOwner.useMutation({
    onSuccess: props.onChanged,
  });
  const removeMutation = trpc.respond.removeAnnounceFromSlotForOwner.useMutation({
    onSuccess: props.onChanged,
  });
  const renameMutation = trpc.respond.renameAnnounceForOwner.useMutation({
    onSuccess: () => {
      setIsEditingName(false);
      props.onChanged();
    },
  });
  const deleteMutation = trpc.respond.deleteAnnounceForOwner.useMutation({
    onSuccess: props.onChanged,
  });

  const handlePlay = () => {
    if (audio) {
      audio.play();
      return;
    }
    getAudioMutation.mutate(props.announce.id, {
      onSuccess: (url) => {
        const nextAudio = new Audio(url);
        setAudio(nextAudio);
        nextAudio.play();
      },
    });
  };

  const handleSlotChange = (slot: keyof typeof slotKeyMap | undefined) => {
    if (!slot || slot === "0") {
      if (props.slot !== "0") {
        removeMutation.mutate({ slot: Number(props.slot) as 1 | 2 | 3 });
      }
      return;
    }

    assignMutation.mutate({
      announceId: props.announce.id,
      slot: Number(slot) as 1 | 2 | 3,
    });
  };

  return (
    <div className="grid items-center gap-3 rounded-2xl border border-slate-200 p-3 md:grid-cols-[minmax(0,1fr)_auto]">
      <div className="flex min-w-0 items-center gap-2.5">
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-none bg-slate-100 text-slate-900 cursor-pointer hover:bg-slate-200"
          onClick={() => setIsEditingName(true)}
          title="Renommer l'annonce"
          aria-label="Renommer l'annonce"
        >
          <FaPen className="h-4 w-4" />
        </button>
        {isEditingName ? (
          <Input
            value={name}
            onChange={setName}
            onBlur={() => {
              if (!name.trim() || name === props.announce.name) {
                setIsEditingName(false);
                setName(props.announce.name);
                return;
              }
              renameMutation.mutate({
                announceId: props.announce.id,
                name: name.trim(),
              });
            }}
          />
        ) : (
          <span>{props.announce.name}</span>
        )}
      </div>

      <div className="flex items-center gap-2 max-md:flex-wrap">
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-none bg-slate-100 text-slate-900 cursor-pointer hover:bg-slate-200"
          onClick={handlePlay}
          title="Lire l'annonce"
          aria-label="Lire l'annonce"
        >
          <FaPlay className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-none bg-slate-100 text-slate-900 cursor-pointer hover:bg-slate-200"
          onClick={() => {
            audio?.pause();
          }}
          title="Arrêter la lecture"
          aria-label="Arrêter la lecture"
        >
          <FaStop className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-none bg-red-100 text-red-700 cursor-pointer hover:bg-red-200"
          onClick={() => {
            if (!confirm("Voulez-vous vraiment supprimer cette annonce ?")) {
              return;
            }
            deleteMutation.mutate(props.announce.id);
          }}
          title="Supprimer l'annonce"
          aria-label="Supprimer l'annonce"
        >
          <FaTrashCan className="h-4 w-4" />
        </button>
        <Select2
          keyMap={slotKeyMap}
          selected={props.slot}
          onSelect={handleSlotChange}
          className="min-w-[132px]"
          defaultString=""
        />
      </div>
    </div>
  );
}

export default function RespondAnnouncesPanel(
  props: RespondAnnouncesPanelProps,
) {
  return (
    <section className="rounded-[22px] bg-white/90 p-[22px] shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
      <div className="text-base font-bold text-slate-900">Annonces disponibles</div>
      <div className="mt-3 grid gap-2.5">
        {props.announces.map((announce) => (
          <AnnounceRow
            key={announce.id}
            ownerId={props.ownerId}
            announce={announce}
            slot={slotFromSettings(announce.id, props.announceSettings)}
            onChanged={props.onChanged}
          />
        ))}
      </div>
      {props.announces.length === 0 ? (
        <div className="mt-3 text-slate-500">
          Aucune annonce enregistrée pour l’instant.
        </div>
      ) : null}
    </section>
  );
}